import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Shield, Heart, CheckCircle, Star } from 'lucide-react'
import { useApi } from '../../contexts/ApiContext'
import { trackPublicPoliciesView, trackQuoteRequest, openChatWithContext } from '../../utils/eventTracking'

const Policies = () => {
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState('health')
  const { getPolicyTypes, getPolicyTypesByCategory } = useApi()
  const [healthPolicies, setHealthPolicies] = useState([])
  const [lifePolicies, setLifePolicies] = useState([])
  const [loading, setLoading] = useState(true)

  // Track page view on mount
  useEffect(() => {
    trackPublicPoliciesView()
  }, [])

  // Handle URL parameters for tab selection
  useEffect(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam === 'health' || tabParam === 'life') {
      setActiveTab(tabParam)
    }
  }, [searchParams])

  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        setLoading(true)
        const allPolicyTypes = await getPolicyTypes()
        
        // Filter health policies
        const healthTypes = allPolicyTypes.filter(type => 
          type.product_category === 'HEALTH' || 
          type.product_category === 'Health Insurance' ||
          type.product_category?.toLowerCase().includes('health')
        )
        
        // Filter life policies
        const lifeTypes = allPolicyTypes.filter(type => 
          type.product_category === 'LIFE' || 
          type.product_category === 'Life Insurance' ||
          type.product_category?.toLowerCase().includes('life')
        )

        // Transform health policies
        const transformedHealthPolicies = healthTypes.map((type, index) => ({
          name: type.display_name || type.product_name || type.policy_type_name,
          category: type.product_category || 'Health Insurance',
          coverage: `₹${(type.base_sum_insured_min / 100000).toFixed(0)} Lakh`,
          premium: `₹${Math.round(type.base_sum_insured_min * 0.001)}/month`, // Approximate premium calculation
          features: [
            'Cashless treatment',
            'Pre & post hospitalization',
            'Emergency ambulance',
            ...(type.coverage_details ? [type.coverage_details] : [])
          ],
          popular: index === 1 // Make second policy popular
        }))

        // Transform life policies
        const transformedLifePolicies = lifeTypes.map((type, index) => ({
          name: type.display_name || type.product_name || type.policy_type_name,
          category: type.product_category || 'Life Insurance',
          coverage: `₹${(type.base_sum_insured_min / 100000).toFixed(0)} Lakh`,
          premium: `₹${Math.round(type.base_sum_insured_min * 0.0005)}/month`, // Approximate premium calculation
          features: [
            'Term life insurance',
            'Accidental death benefit',
            'Critical illness cover',
            ...(type.coverage_details ? [type.coverage_details] : [])
          ],
          popular: index === 1 // Make second policy popular
        }))

        setHealthPolicies(transformedHealthPolicies)
        setLifePolicies(transformedLifePolicies)
      } catch (error) {
        console.error('Error fetching policies:', error)
        // Fallback to default policies if API fails
        setHealthPolicies([
          {
            name: 'Health Shield Basic',
            coverage: '₹5 Lakh',
            premium: '₹500/month',
            features: ['Cashless treatment', 'Pre & post hospitalization', 'Emergency ambulance'],
            popular: false
          }
        ])
        setLifePolicies([
          {
            name: 'Life Secure Basic',
            coverage: '₹10 Lakh',
            premium: '₹300/month',
            features: ['Term life insurance', 'Accidental death benefit', 'Critical illness cover'],
            popular: false
          }
        ])
      } finally {
        setLoading(false)
      }
    }

    fetchPolicies()
  }, [getPolicyTypes])

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Our Insurance Policies</h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              Choose from our comprehensive range of health and life insurance policies 
              designed to protect what matters most to you.
            </p>
          </div>
        </div>
      </section>

      {/* Insurance Products Section with Tabs */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tab Navigation */}
          <div className="flex justify-center mb-12">
            <div className="bg-gray-100 rounded-lg p-1 inline-flex">
              <button
                onClick={() => setActiveTab('health')}
                className={`flex items-center px-8 py-4 rounded-md font-semibold text-lg transition-all duration-200 min-w-[200px] justify-center ${
                  activeTab === 'health'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Shield className="h-6 w-6 mr-3" />
                Health Insurance
              </button>
              <button
                onClick={() => setActiveTab('life')}
                className={`flex items-center px-8 py-4 rounded-md font-semibold text-lg transition-all duration-200 min-w-[200px] justify-center ${
                  activeTab === 'life'
                    ? 'bg-white text-red-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Heart className="h-6 w-6 mr-3" />
                Life Insurance
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="transition-all duration-300">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-4 text-gray-600">Loading policies...</p>
              </div>
            ) : (
              <>
                {activeTab === 'health' && (
                  <div>
                    <div className="text-center mb-12">
                      <div className="flex items-center justify-center mb-4">
                        <Shield className="h-12 w-12 text-blue-600 mr-4" />
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Health Insurance</h2>
                      </div>
                      <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Comprehensive health coverage with cashless treatment at 500+ network hospitals
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                      {healthPolicies.map((policy, index) => (
                    <div key={index} className={`card hover:shadow-lg hover:scale-105 transition-all duration-300 ${
                      policy.popular ? 'ring-2 ring-blue-500 relative' : ''
                    }`}>
                      {policy.popular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                            Most Popular
                          </span>
                        </div>
                      )}
                      <div className="text-center mb-6">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{policy.name}</h3>
                        <div className="text-sm text-blue-600 font-medium mb-2">Product Category: {policy.category}</div>
                        <div className="text-3xl font-bold text-blue-600 mb-2">{policy.coverage}</div>
                        <div className="text-lg text-gray-600">{policy.premium}</div>
                      </div>
                      <ul className="space-y-3 mb-8">
                        {policy.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-center">
                            <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <button 
                        onClick={() => {
                          trackQuoteRequest({
                            policyType: policy.name,
                            category: policy.category,
                            coverageAmount: policy.coverage
                          })
                          openChatWithContext(
                            `I would like to get a quote for ${policy.name} with coverage of ${policy.coverage}.`,
                            'policies_page_health'
                          )
                        }}
                        className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors duration-200 ${
                        policy.popular 
                          ? 'bg-blue-600 text-white hover:bg-blue-700' 
                          : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                      }`}>
                        Get Quote
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

                {activeTab === 'life' && (
                  <div>
                    <div className="text-center mb-12">
                      <div className="flex items-center justify-center mb-4">
                        <Heart className="h-12 w-12 text-red-600 mr-4" />
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Life Insurance</h2>
                      </div>
                      <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Secure your family's future with comprehensive life insurance coverage
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                      {lifePolicies.map((policy, index) => (
                    <div key={index} className={`card hover:shadow-lg hover:scale-105 transition-all duration-300 ${
                      policy.popular ? 'ring-2 ring-red-500 relative' : ''
                    }`}>
                      {policy.popular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <span className="bg-red-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                            Most Popular
                          </span>
                        </div>
                      )}
                      <div className="text-center mb-6">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{policy.name}</h3>
                        <div className="text-sm text-red-600 font-medium mb-2">Product Category: {policy.category}</div>
                        <div className="text-3xl font-bold text-red-600 mb-2">{policy.coverage}</div>
                        <div className="text-lg text-gray-600">{policy.premium}</div>
                      </div>
                      <ul className="space-y-3 mb-8">
                        {policy.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-center">
                            <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <button 
                        onClick={() => {
                          trackQuoteRequest({
                            policyType: policy.name,
                            category: policy.category,
                            coverageAmount: policy.coverage
                          })
                          openChatWithContext(
                            `I would like to get a quote for ${policy.name} with coverage of ${policy.coverage}.`,
                            'policies_page_life'
                          )
                        }}
                        className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors duration-200 ${
                        policy.popular 
                          ? 'bg-red-600 text-white hover:bg-red-700' 
                          : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                      }`}>
                        Get Quote
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}

export default Policies
